import { Dialog as MuiDialog } from "@mui/material";
import type { DialogProps as MuiDialogProps } from "@mui/material";
import type { ReactNode } from "react";
import { memo } from "react";
import { RxCross2 } from "react-icons/rx";
import { twMerge } from "tailwind-merge";

export enum CloseReason {
    BACKDROP_CLICK = "backdropClick",
    ESCAPE_KEY_DOWN = "escapeKeyDown",
    CLOSE_BUTTON = "closeButton",
}

interface DialogProps extends Omit<MuiDialogProps, "title"> {
    open: boolean;
    children: ReactNode;
    onClose: (event: Event, reason: CloseReason | string) => void | undefined;
    title?: ReactNode;
}

const paperClassName =
    "dialog-paper-bg drop-shadow-[4px_4px_15.2px_rgba(0,0,0,0.05)] rounded-[12px] py-[25px] px-[35px] relative max-h-[80vh] overflow-y-hidden min-h-[200px] max-w-[1000px] border-0 border-primary/20 border-[1px]";

function DialogComponent({ children, title, ...props }: DialogProps) {
    return (
        <MuiDialog
            {...props}
            container={() => document.getElementById("app-theme-root")}
            PaperProps={{
                ...props.PaperProps,
                className: twMerge("min-w-[300px] sm:min-w-[400px]", props.PaperProps?.className, paperClassName),
            }}
            slotProps={{
                backdrop: {
                    className: "backdrop-blur-[10px] dialog-backdrop-bg",
                },
            }}
            transitionDuration={100}
        >
            <div className="flex w-full min-h-0 flex-col overflow-y-visible">
                <div className="flex items-start justify-between gap-4 dialog-paper-bg pb-4">
                    {title ? <h2 className="text-[20px] leading-[100%] font-medium text-primary">{title}</h2> : <div />}
                    <RxCross2
                        className="dialog-paper-bg min-w-5 min-h-5 cursor-pointer text-primary opacity-50 hover:text-[var(--dialog-close-hover)] hover:opacity-100 transition-colors -mr-2"
                        onClick={(event) => props.onClose?.(event as unknown as Event, CloseReason.CLOSE_BUTTON)}
                    />
                </div>
                <div className="w-full min-h-0">
                    <div className="text-dark">{children}</div>
                </div>
            </div>
        </MuiDialog>
    );
}

export const Dialog = memo(DialogComponent);

Dialog.displayName = "Dialog";
